import { AbstractControl, FormControl, FormGroup, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { FullBackupData, IncrementalData } from 'mbs-ui-shared@models/backup/schedule-models';
import { Period } from 'mbs-ui-shared@models/Period.enum';
import moment from 'moment/moment';
import { Schedule } from 'wizards/common/schedule.class';
import {
  GFSPolicySettingsForm,
  MonthlyStorageTypeForm,
  WeeklyStorageTypeForm,
  YearlyStorageTypeForm
} from 'wizards/models/retention-policy-models';

const convertPeriodToMoment: (startPoint: moment.Moment, period: { count: number; period: Period }) => moment.Moment = (
  startPoint,
  period
) => {
  if (period.period === Period.day) {
    return startPoint.clone().add(period.count, 'days');
  }

  if (period.period === Period.week) {
    return startPoint.clone().add(period.count, 'weeks');
  }

  if (period.period === Period.month) {
    return startPoint.clone().add(period.count, 'months');
  }

  return startPoint.clone().add(period.count, 'years');
};

/**
 * Also, according to BACKUP-13072, calculation of the minimum schedule period should follow this logic:
 * *
 * * Monthly = 30*Repeat every
 * * Weekly, one day selected = 7*Repeat every
 * * Weekly, several days = minimum interval between days.
 * *   e.g. Monday, Tuesday = 1;
 * *   Monday, Wednesday = 2;
 * *   Monday, Thursday = 3;
 * @param {Object} schedule
 * @param {Object} selected
 * @return {boolean}
 */
const isKeepBackupForCorrect: (
  schedule: IncrementalData | FullBackupData,
  selected: { count: number; period: number }
) => { error: boolean; count: number; period: number } = (schedule, selected) => {
  const isMonthMode = schedule.recurringPeriod === 'Monthly';
  const multiplications = {
    [Period.day]: 1 * ((isMonthMode ? schedule.repeatEveryCount : 1) || 1),
    [Period.week]: 7 * ((isMonthMode ? schedule.repeatEveryCount : 1) || 1),
    [Period.month]: 30 * ((isMonthMode ? schedule.repeatEveryCount : 1) || 1),
    [Period.year]: 365 * ((isMonthMode ? schedule.repeatEveryCount : 1) || 1)
  };
  let isInvalidData = false;
  let selectedDaysLength;
  let oneDaySelected;
  let severalDayMinLength;
  let expectedCountInDays: number;
  const selectedCountInDays = selected.count * multiplications[selected.period];

  if (!isMonthMode && schedule.weeklyDayOfWeek) {
    selectedDaysLength = schedule.weeklyDayOfWeek?.filter((checked) => !!checked).length;
    oneDaySelected = selectedDaysLength <= 1;

    if (!oneDaySelected) {
      severalDayMinLength = new Schedule({ type: 'Daily', days: [...schedule.weeklyDayOfWeek] }).getMinDailyPeriod();
    }
  }

  if (isMonthMode) {
    expectedCountInDays = multiplications[Period.month] * schedule.repeatEveryCount;
  } else {
    isInvalidData = !selectedDaysLength;
    expectedCountInDays = oneDaySelected ? multiplications[Period.week] * schedule.repeatEveryCount : severalDayMinLength;
  }

  if (isInvalidData || expectedCountInDays <= selectedCountInDays) {
    return {
      error: false,
      count: null,
      period: null
    };
  }

  return {
    error: true,
    count: isMonthMode ? Math.floor(expectedCountInDays / multiplications[Period.month]) : expectedCountInDays,
    period: isMonthMode ? Period.month : Period.day
  };
};

const weeklyMoreMYValidator: (GFSPolicySettings: AbstractControl, control: AbstractControl) => ValidationErrors | null = (
  GFSPolicySettings,
  control
) => {
  if (!GFSPolicySettings || !control.value) {
    return null;
  }

  const monthly = GFSPolicySettings.get('monthly').value;
  const yearly = GFSPolicySettings.get('yearly').value;
  let invalidMonthly = false;
  let invalidYearly = false;
  const weeks = moment().add(control.value, 'weeks');

  if (monthly && monthly.enabled) {
    const months = moment().add(monthly.storePeriod, 'months');

    invalidMonthly = months.isBefore(weeks);
  }

  if (yearly && yearly.enabled) {
    const years = moment().add(yearly.storePeriod, 'years');

    invalidYearly = years.isBefore(weeks);
  }

  return invalidMonthly ? { moreThanMonthly: true } : invalidYearly ? { moreThanYearly: true } : null;
};

const monthlyMoreYValidator: (GFSPolicySettings: AbstractControl, control: AbstractControl) => ValidationErrors | null = (
  GFSPolicySettings,
  control
) => {
  if (!GFSPolicySettings || !control.value) {
    return null;
  }

  const yearly = GFSPolicySettings.get('yearly').value;
  const invalidYearly = yearly && yearly.enabled && yearly.storePeriod < control.value / 12;

  return invalidYearly ? { moreThanYearly: true } : null;
};

const getGFSPolicyFormGroup: (weeklyValidator: ValidatorFn, monthlyValidator: ValidatorFn) => FormGroup<GFSPolicySettingsForm> = (
  weeklyValidator,
  monthlyValidator
) => {
  return new FormGroup<GFSPolicySettingsForm>({
    enabled: new FormControl(false),
    enabledImmutability: new FormControl(false),
    weekly: new FormGroup<WeeklyStorageTypeForm>({
      enabled: new FormControl(false),
      storePeriod: new FormControl(1, [Validators.min(1), weeklyValidator])
    }),
    monthly: new FormGroup<MonthlyStorageTypeForm>({
      enabled: new FormControl(false),
      storePeriod: new FormControl(1, [Validators.min(1), monthlyValidator])
    }),
    yearly: new FormGroup<YearlyStorageTypeForm>({
      enabled: new FormControl(false),
      storePeriod: new FormControl(1, [Validators.min(1)]),
      takeBackupFromMonth: new FormControl(1)
    })
  });
};

const moreThatRetention: (
  value: { count: number; period: Period },
  retention: { count: number; period: Period },
  equalIsOk?: boolean
) => boolean = (value, retention, equalIsOk = true) => {
  const startPoint = moment();
  const valuePoint = convertPeriodToMoment(startPoint, value);
  const retentionPoint = convertPeriodToMoment(startPoint, retention);

  if (valuePoint.isSame(retentionPoint)) {
    return equalIsOk;
  }

  if (valuePoint.isAfter(retentionPoint)) {
    return true;
  }

  return false;
};

export { getGFSPolicyFormGroup, isKeepBackupForCorrect, monthlyMoreYValidator, moreThatRetention, weeklyMoreMYValidator };
